// src/pages/Careers/components/AvailableRoles.jsx
import React, { useState } from "react";
import { ArrowRight, Code2, Briefcase, Cpu, Layers, Sparkles } from "lucide-react";

const ROLES_DATA = [
  // Row 1
  {
    id: "web-developer",
    title: "Web Developer",
    domain: "Technical",
    location: "Chennai / Hybrid",
    experience: "0–2 Years / Freshers",
    skills: ["React.js", "Next.js", "Tailwind CSS", "JavaScript ES6+"],
    description: "Craft modern, responsive, and high-performance web applications and landing experiences.",
    icon: Code2,
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: "application-developer",
    title: "Application Developer",
    domain: "Technical",
    location: "Chennai / Hybrid",
    experience: "0–2 Years",
    skills: ["Node.js", "Full-Stack", "REST APIs", "MongoDB"],
    description: "Build scalable web and mobile software applications powered by robust backend services.",
    icon: Layers,
    image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: "python-developer",
    title: "Python Developer",
    domain: "Technical",
    location: "Chennai / Hybrid",
    experience: "0–2 Years",
    skills: ["Python", "FastAPI", "Django", "Automation"],
    description: "Develop automated backend systems, data pipelines, and core Python microservices.",
    icon: Cpu,
    image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: "data-analyst",
    title: "Data Analyst",
    domain: "Technical",
    location: "Chennai / Remote",
    experience: "0–2 Years",
    skills: ["SQL", "Pandas", "Data Visualization", "Power BI"],
    description: "Analyze business metrics, build interactive dashboards, and deliver actionable insights.",
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=800&auto=format&fit=crop",
  },

  // Row 2
  {
    id: "machine-learning-engineer",
    title: "Machine Learning Engineer",
    domain: "Technical",
    location: "Chennai / Hybrid",
    experience: "0–2 Years",
    skills: ["PyTorch", "Scikit-Learn", "Model Training", "Data Ops"],
    description: "Design, train, and deploy predictive machine learning models into production systems.",
    icon: Cpu,
    image: "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: "ai-engineer",
    title: "AI Engineer",
    domain: "Technical",
    location: "Chennai / Hybrid",
    experience: "0–2 Years",
    skills: ["LLMs", "LangChain", "Vector DBs", "OpenAI APIs"],
    description: "Integrate LLM AI agents, RAG workflows, and intelligent microservices into software products.",
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1677442136019-21780efad99a?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: "ui-ux-designer",
    title: "UI/UX Designer",
    domain: "Technical",
    location: "Chennai / Hybrid",
    experience: "0–2 Years",
    skills: ["Figma", "Design Systems", "Prototyping", "User Flows"],
    description: "Design intuitive visual interfaces, wireframes, and smooth digital user journeys.",
    icon: Code2,
    image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: "bda",
    title: "Business Development Associate (BDA)",
    domain: "Non-Technical",
    location: "Chennai / Remote",
    experience: "0–2 Years",
    skills: ["Lead Generation", "Client Pitching", "Sales Strategy", "CRM"],
    description: "Drive strategic growth by connecting with prospective clients and presenting digital software solutions.",
    icon: Briefcase,
    image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=800&auto=format&fit=crop",
  },
];

const AvailableRoles = ({ onOpenRoleModal }) => {
  const [hoveredCardId, setHoveredCardId] = useState(null);

  const row1Roles = ROLES_DATA.slice(0, 4);
  const row2Roles = ROLES_DATA.slice(4, 8);

  const renderAccordionRow = (rolesList) => {
    return (
      <div className="flex flex-col lg:flex-row gap-4 h-auto lg:h-[26rem] w-full mb-6">
        {rolesList.map((role) => {
          const IconComp = role.icon;
          const isHovered = hoveredCardId === role.id;

          return (
            <div
              key={role.id}
              onMouseEnter={() => setHoveredCardId(role.id)}
              onMouseLeave={() => setHoveredCardId(null)}
              className={`relative rounded-3xl overflow-hidden bg-gray-900 transition-all duration-500 ease-out h-[22rem] lg:h-full border border-gray-800 shadow-md hover:shadow-2xl cursor-pointer group flex flex-col justify-between p-6 ${
                isHovered ? "lg:flex-[2.8]" : "lg:flex-[1]"
              }`}
            >
              {/* Full Background Cover Image */}
              <img
                src={role.image}
                alt={role.title}
                className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
              />

              {/* Dark Gradient Overlay over Image */}
              <div
                className={`absolute inset-0 transition-all duration-500 ${
                  isHovered
                    ? "bg-gradient-to-t from-black/95 via-black/70 to-black/40"
                    : "bg-gradient-to-t from-black/90 via-black/40 to-black/20"
                }`}
              />

              {/* Top Bar: Icon Circle Badge */}
              <div className="relative z-10 flex items-center justify-end">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-md text-white flex items-center justify-center border border-white/20 shrink-0 shadow-sm">
                  <IconComp className="w-4 h-4" />
                </div>
              </div>

              {/* Bottom Area: Text OVER Image & Expanding Details */}
              <div className="relative z-10 space-y-3">
                {/* Role Title & Subtitle */}
                <div>
                  <h3
                    className={`font-bold text-white transition-all duration-300 ${
                      isHovered ? "text-2xl md:text-3xl leading-tight" : "text-lg line-clamp-1"
                    }`}
                  >
                    {role.title}
                  </h3>
                  <p className="text-xs text-gray-300 font-medium mt-1">
                    {role.experience}
                  </p>
                </div>

                {/* Expanded Content (Shows full description & skill pills when hovered) */}
                <div
                  className={`transition-all duration-500 ease-in-out overflow-hidden ${
                    isHovered ? "max-h-60 opacity-100 space-y-3 mt-2" : "max-h-0 opacity-0"
                  }`}
                >
                  <p className="text-gray-300 text-xs md:text-sm leading-relaxed font-normal">
                    {role.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5">
                    {role.skills.map((skill, sIdx) => (
                      <span
                        key={sIdx}
                        className="px-2.5 py-1 rounded-md text-[10px] font-medium bg-white/15 backdrop-blur-md text-white border border-white/20"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Apply Button / Action CTA */}
                <div className="pt-2">
                  {isHovered ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenRoleModal(role.title, role.domain);
                      }}
                      className="px-6 py-2.5 rounded-full bg-white text-gray-900 hover:bg-gray-100 font-bold text-xs inline-flex items-center gap-2 shadow-lg transition duration-300"
                    >
                      <span>APPLY NOW</span>
                      <div className="w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center">
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    </button>
                  ) : (
                    <div className="text-[11px] font-semibold text-gray-300 group-hover:text-white flex items-center gap-1">
                      <span>Explore Role</span>
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section id="available-roles" className="py-12 md:py-14 bg-gray-50/70 text-gray-900 font-sans relative border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs md:text-sm font-medium border border-gray-300 rounded-full px-3.5 py-1 text-gray-700 bg-white mb-4 inline-block shadow-xs">
            Opportunities
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light leading-snug text-gray-900 mb-4">
            Available Roles
          </h2>
          <p className="text-lg text-gray-600 font-normal">
            Explore engineering and growth positions across our core development teams.
          </p>
        </div>

        {/* Horizontal Accordion Rows */}
        <div>
          {renderAccordionRow(row1Roles)}
          {renderAccordionRow(row2Roles)}
        </div>
      </div>
    </section>
  );
};

export default AvailableRoles;
